import { defineStore } from 'pinia'
import Vue, { ref, computed } from 'vue'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { LocalStorageKeys, CookieTypes, SessionStorageKeys } from 'javascript/Constants'
import { useAppStore } from 'Store/app'
import { useIconMessageStore } from 'Store/icon-message'
import { useSettingsStore } from 'Store/settings'
import { removeTriplaUID, setTriplaUID } from 'Utilities/triplaUID'

const genInitialCookiePreferences = () => ({
  confirmed: false,
  functional_cookies_enabled: true,
  performance_enabled: true,
  targeting_and_advertisement_enabled: true
})

export const useCookieConsentStore = defineStore('CookieConsent', () => {
  const appStore = useAppStore()
  const iconMessageStore = useIconMessageStore()
  const settingsStore = useSettingsStore()

  const cookiePreferences = ref(genInitialCookiePreferences())

  try {
    const preferences = JSON.parse(triplaLocalStorage.getItem(LocalStorageKeys.COOKIE_PREFERENCES))
    if (preferences) {
      cookiePreferences.value = preferences
    }
  } catch (e) {
    // do nothing
  }

  const isCookiePreferencesConfirmed = computed(() => cookiePreferences.value.confirmed)

  // in case cookie consent is disabled by settings, all cookie preferences are enabled regardless of values in this store or localStorage
  const isCookiePreferenceEnabled = (preference) =>
    settingsStore.isCookieConsentEnabled ? isCookiePreferencesConfirmed.value && preference : true

  const isFunctionalCookieEnabled = computed(() =>
    isCookiePreferenceEnabled(cookiePreferences.value.functional_cookies_enabled)
  )
  const isPerformanceCookieEnabled = computed(() =>
    isCookiePreferenceEnabled(cookiePreferences.value.performance_enabled)
  )
  const isAdvertisingCookieEnabled = computed(() =>
    isCookiePreferenceEnabled(cookiePreferences.value.targeting_and_advertisement_enabled)
  )

  function setCookiePreferences(value) {
    if (!settingsStore.isCookieConsentEnabled) return

    if (Array.isArray(value)) {
      cookiePreferences.value = {
        ...cookiePreferences.value,
        confirmed: value.includes(CookieTypes.NECESSARY),
        functional_cookies_enabled: value.includes(CookieTypes.FUNCTIONAL),
        performance_enabled: value.includes(CookieTypes.PERFORMANCE),
        targeting_and_advertisement_enabled: value.includes(CookieTypes.ADVERTISING)
      }
    } else {
      cookiePreferences.value = {
        ...cookiePreferences.value,
        ...(value.persisted ? value : genInitialCookiePreferences())
      }
    }

    triplaLocalStorage.setItem(
      LocalStorageKeys.COOKIE_PREFERENCES,
      JSON.stringify(cookiePreferences.value)
    )

    if (isPerformanceCookieEnabled.value) {
      Vue.prototype.$gtag.enableTracking()
    } else {
      Vue.prototype.$gtag.disableTracking()
    }

    if (isFunctionalCookieEnabled.value) {
      if (appStore.chatChannel) {
        triplaLocalStorage.setItem(LocalStorageKeys.SELECTED_CHAT_CHANNEL, appStore.chatChannel)
      }
      triplaLocalStorage.setItem(LocalStorageKeys.CHAT_ICON_MINIZED, appStore.isMinimized)
      triplaLocalStorage.setItem(
        LocalStorageKeys.CLOSED_ICON_MESSAGES,
        JSON.stringify(iconMessageStore.closedIconMessages)
      )
    }

    if (isAdvertisingCookieEnabled.value) {
      if (sessionStorage.getItem(SessionStorageKeys.TEMP_TRIPLA_UID)) {
        setTriplaUID(sessionStorage.getItem(SessionStorageKeys.TEMP_TRIPLA_UID))
      }
    } else {
      removeTriplaUID()
    }
  }

  function clearCookiePreferences() {
    cookiePreferences.value = genInitialCookiePreferences()
  }

  function acceptAllCookies() {
    setCookiePreferences(Object.values(CookieTypes))
  }

  return {
    cookiePreferences,

    isCookiePreferencesConfirmed,
    isFunctionalCookieEnabled,
    isPerformanceCookieEnabled,
    isAdvertisingCookieEnabled,

    acceptAllCookies,
    clearCookiePreferences,
    setCookiePreferences
  }
})
